
import { useState } from "react";
import { Button } from "@/components/ui/button";
import {
  Star,
  ChevronLeft,
  Share2,
  Download,
  Copy,
  Bookmark,
  BookmarkCheck
} from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";

interface ChatHeaderProps {
  promptTitle: string | null;
  promptId: string | null;
  sessionId: string | null;
  onSaveToFavorites?: () => void;
  isSavingToFavorites?: boolean;
  onBackToTemplates?: () => void;
}

const ChatHeader = ({
  promptTitle,
  promptId,
  sessionId,
  onSaveToFavorites,
  isSavingToFavorites = false,
  onBackToTemplates
}: ChatHeaderProps) => {
  const [isFavorite, setIsFavorite] = useState(false);
  
  const handleSaveToFavorites = () => {
    if (onSaveToFavorites) {
      onSaveToFavorites();
      setIsFavorite(!isFavorite);
    }
  };
  
  const copyConversationLink = () => {
    if (sessionId) {
      const url = `${window.location.origin}/product-copilot?session=${sessionId}`;
      navigator.clipboard.writeText(url);
    }
  };
  
  return (
    <div className="border-b p-4 bg-white dark:bg-gray-900 flex items-center justify-between">
      <div className="flex items-center">
        {onBackToTemplates && (
          <Button
            variant="ghost"
            size="icon"
            className="mr-2"
            onClick={onBackToTemplates}
          >
            <ChevronLeft className="h-5 w-5" />
          </Button>
        )}
        
        <div>
          <h3 className="font-medium">{promptTitle || "New Conversation"}</h3>
          {promptId && (
            <p className="text-xs text-muted-foreground">
              Template-guided conversation
            </p>
          )}
          {sessionId && !promptId && (
            <p className="text-xs text-muted-foreground">
              Free-form conversation
            </p>
          )}
        </div>
      </div>
      
      <div className="flex items-center gap-2">
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                variant="ghost"
                size="icon"
                onClick={handleSaveToFavorites}
                disabled={!promptId || isSavingToFavorites}
              >
                {isFavorite || isSavingToFavorites ? (
                  <BookmarkCheck className="h-5 w-5 text-amber-500" />
                ) : (
                  <Bookmark className="h-5 w-5" />
                )}
              </Button>
            </TooltipTrigger>
            <TooltipContent>
              {isFavorite ? "Remove from favorites" : "Save to favorites"}
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
        
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" size="icon">
              <Share2 className="h-5 w-5" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuItem onClick={copyConversationLink} disabled={!sessionId}>
              <Copy className="h-4 w-4 mr-2" />
              Copy link to conversation
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuItem>
              <Download className="h-4 w-4 mr-2" />
              Export conversation
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </div>
  );
};

export default ChatHeader;
